/**
 * KOT match point windows from pose heuristics, motion wake, or ball FSM.
 * Priority: pose_heuristics > motion_wake > ball_fsm.
 */

import {
  pointSegmentsFromPoseResult,
  resolvePointSegmentsFromPoseResult,
} from "./kotPointSegmentsFromPose";

const DEFAULT_OPTS = Object.freeze({
  prepPadMs: 400,
  postPadMs: 800,
  minDurationMs: 1200,
  mergeGapMs: 1500,
});

function num(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function sortedStates(states) {
  return (Array.isArray(states) ? states : [])
    .map((s) => ({
      t_ms: num(s?.t_ms, 0),
      state: String(s?.state || "").trim().toLowerCase(),
    }))
    .filter((s) => s.state)
    .sort((a, b) => a.t_ms - b.t_ms);
}

function isInPlayState(state) {
  return state === "service" || state === "rally";
}

/**
 * Raw windows from ball FSM state transitions (no padding).
 * @returns {Array<{ start_ms: number, end_ms: number }>}
 */
export function rawPointWindowsFromBallStates(states = []) {
  const list = sortedStates(states);
  if (!list.length) return [];

  const windows = [];
  let openStart = null;
  let prevState = "waiting";

  for (const row of list) {
    const st = row.state;
    if (isInPlayState(st) && !isInPlayState(prevState)) {
      openStart = row.t_ms;
    } else if (!isInPlayState(st) && isInPlayState(prevState) && openStart != null) {
      windows.push({ start_ms: openStart, end_ms: row.t_ms });
      openStart = null;
    }
    prevState = st;
  }

  if (openStart != null) {
    const lastT = list[list.length - 1]?.t_ms ?? openStart;
    windows.push({ start_ms: openStart, end_ms: lastT });
  }

  return windows;
}

function countStrokesInWindow(events, startMs, endMs) {
  if (!Array.isArray(events) || !events.length) return null;
  let n = 0;
  for (const ev of events) {
    if (String(ev?.type || "") !== "BEGIN_STROKE") continue;
    const t = num(ev.t_ms);
    if (t == null) continue;
    if (t >= startMs && t <= endMs) n += 1;
  }
  return n > 0 ? n : null;
}

function mergeCloseWindows(windows, mergeGapMs) {
  if (!windows.length) return [];
  const sorted = [...windows].sort((a, b) => a.start_ms - b.start_ms);
  const out = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = out[out.length - 1];
    const cur = sorted[i];
    if (cur.start_ms - prev.end_ms <= mergeGapMs) {
      prev.end_ms = Math.max(prev.end_ms, cur.end_ms);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/**
 * @param {object[]} states
 * @param {object[]} [events]
 * @param {number} [durationMs]
 * @param {object} [opts]
 * @returns {Array<{ index: number, start_ms: number, end_ms: number, duration_ms: number, stroke_hint_count: number|null, source: string }>}
 */
export function pointSegmentsFromBallResult(
  { states = [], events = [] } = {},
  durationMs = null,
  opts = {}
) {
  const o = { ...DEFAULT_OPTS, ...opts };
  const raw = rawPointWindowsFromBallStates(states);
  if (!raw.length) return [];

  const maxEnd =
    num(durationMs) ??
    Math.max(
      sortedStates(states).at(-1)?.t_ms ?? 0,
      ...(Array.isArray(events)
        ? events.map((e) => num(e?.t_ms, 0))
        : [0])
    );

  const padded = raw.map((w) => ({
    start_ms: Math.max(0, w.start_ms - o.prepPadMs),
    end_ms: Math.min(maxEnd + o.postPadMs, w.end_ms + o.postPadMs),
  }));

  const merged = mergeCloseWindows(padded, o.mergeGapMs).filter(
    (w) => w.end_ms - w.start_ms >= o.minDurationMs
  );

  return merged.map((w, index) => ({
    index: index + 1,
    start_ms: Math.round(w.start_ms),
    end_ms: Math.round(w.end_ms),
    duration_ms: Math.round(w.end_ms - w.start_ms),
    stroke_hint_count: countStrokesInWindow(events, w.start_ms, w.end_ms),
    source: "ball_fsm",
  }));
}

function normalizeStoredSegments(stored, defaultSource) {
  if (!Array.isArray(stored) || !stored.length) return [];
  return stored
    .map((s, i) => ({
      index: num(s.index, i + 1),
      start_ms: num(s.start_ms, 0),
      end_ms: num(s.end_ms, 0),
      duration_ms: num(s.duration_ms, num(s.end_ms, 0) - num(s.start_ms, 0)),
      stroke_hint_count: s.stroke_hint_count ?? null,
      source: s.source || defaultSource,
    }))
    .filter((s) => s.end_ms > s.start_ms);
}

/**
 * Motion wake / activity islands → point segments.
 */
export function pointSegmentsFromMotionResult(
  segmentResult,
  durationSec = null,
  opts = {}
) {
  if (!segmentResult || typeof segmentResult !== "object") return [];
  const stored = segmentResult.motion_point_segments;
  if (Array.isArray(stored) && stored.length) {
    return normalizeStoredSegments(stored, "motion_wake");
  }
  const islands =
    segmentResult.activity_islands ||
    segmentResult.active_play_windows ||
    [];
  if (!islands.length) return [];

  const durationMs =
    durationSec != null && Number.isFinite(Number(durationSec))
      ? Math.round(Number(durationSec) * 1000)
      : num(segmentResult.duration_ms) ??
        Math.round((Number(segmentResult.duration_seconds) || 0) * 1000);

  const o = { ...DEFAULT_OPTS, ...opts };
  const raw = islands.map((island) => {
    const startSec =
      num(island.start_ms) != null
        ? num(island.start_ms) / 1000
        : num(island.start_seconds, 0);
    const endSec =
      num(island.end_ms) != null
        ? num(island.end_ms) / 1000
        : num(island.end_seconds, startSec);
    return {
      start_ms: Math.round(startSec * 1000),
      end_ms: Math.round(endSec * 1000),
      stroke_hint_count: island.stroke_hint_count ?? null,
    };
  });

  const padded = raw.map((w) => ({
    start_ms: Math.max(0, w.start_ms - o.prepPadMs),
    end_ms: Math.min(
      (durationMs || w.end_ms) + o.postPadMs,
      w.end_ms + o.postPadMs
    ),
    stroke_hint_count: w.stroke_hint_count,
  }));

  const merged = mergeCloseWindows(padded, o.mergeGapMs).filter(
    (w) => w.end_ms - w.start_ms >= o.minDurationMs
  );

  return merged.map((w, index) => ({
    index: index + 1,
    start_ms: Math.round(w.start_ms),
    end_ms: Math.round(w.end_ms),
    duration_ms: Math.round(w.end_ms - w.start_ms),
    stroke_hint_count: w.stroke_hint_count,
    source: "motion_wake",
  }));
}

export function resolvePointSegmentsFromMotionResult(
  segmentResult,
  durationSec = null
) {
  return pointSegmentsFromMotionResult(segmentResult, durationSec);
}

/**
 * Unified resolver: pose > motion > ball.
 */
export function resolveKotPointSegments(
  { poseResult = null, segmentResult = null, ballResult = null } = {},
  durationSec = null
) {
  const poseSegs = resolvePointSegmentsFromPoseResult(poseResult, durationSec);
  if (poseSegs.length) {
    return { segments: poseSegs, source: "pose_heuristics" };
  }
  const mergedPoseSegs = normalizeStoredSegments(
    segmentResult?.pose_point_segments,
    "pose_heuristics"
  );
  if (mergedPoseSegs.length) {
    return { segments: mergedPoseSegs, source: "pose_heuristics" };
  }
  const motionSegs = resolvePointSegmentsFromMotionResult(
    segmentResult,
    durationSec
  );
  if (motionSegs.length) {
    return { segments: motionSegs, source: "motion_wake" };
  }
  const ballSegs = resolvePointSegmentsFromBallResult(ballResult, durationSec);
  if (ballSegs.length) {
    return { segments: ballSegs, source: "ball_fsm" };
  }
  return { segments: [], source: null };
}

export function kotSegmentSourceLabel(source) {
  if (source === "pose_heuristics") return "Pose";
  if (source === "motion_wake") return "Motion";
  if (source === "ball_fsm") return "Ball";
  return null;
}

/**
 * Prefer stored segments; otherwise compute from states/events.
 */
export function resolvePointSegmentsFromBallResult(ballResult, durationSec = null) {
  if (!ballResult || typeof ballResult !== "object") return [];
  const stored = ballResult.point_segments;
  if (Array.isArray(stored) && stored.length) {
    return normalizeStoredSegments(stored, "ball_fsm");
  }
  const durationMs =
    durationSec != null && Number.isFinite(Number(durationSec))
      ? Math.round(Number(durationSec) * 1000)
      : null;
  return pointSegmentsFromBallResult(
    {
      states: ballResult.states || [],
      events: ballResult.events || [],
    },
    durationMs
  );
}

export function findPointSegmentIndex(segments, timeSec) {
  const tMs = Math.round((Number(timeSec) || 0) * 1000);
  for (let i = 0; i < segments.length; i += 1) {
    const s = segments[i];
    if (tMs >= s.start_ms && tMs < s.end_ms) return i;
  }
  return -1;
}

/** Start of previous (−1) or next (+1) point window, in seconds. */
export function adjacentPointSeekSec(segments, timeSec, direction) {
  if (!Array.isArray(segments) || !segments.length) return null;
  const dir = direction < 0 ? -1 : 1;
  const tSec = Number(timeSec) || 0;
  const tMs = Math.round(tSec * 1000);
  const idx = findPointSegmentIndex(segments, tSec);

  if (idx >= 0) {
    if (dir < 0) {
      const startSec = segments[idx].start_ms / 1000;
      if (tSec - startSec > 0.4) return startSec;
      const prev = segments[idx - 1];
      return prev ? prev.start_ms / 1000 : startSec;
    }
    const next = segments[idx + 1];
    return next ? next.start_ms / 1000 : null;
  }

  if (dir > 0) {
    const next = segments.find((s) => s.start_ms > tMs);
    return next ? next.start_ms / 1000 : null;
  }
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    if (segments[i].end_ms <= tMs) return segments[i].start_ms / 1000;
  }
  return segments[0].start_ms / 1000;
}

/** Next segment start (sec) when playhead passes end of current segment. */
export function nextPointSeekSec(segments, timeSec, { endSlackMs = 80 } = {}) {
  if (!segments.length) return null;
  const tMs = Math.round((Number(timeSec) || 0) * 1000);
  for (let i = 0; i < segments.length; i += 1) {
    const s = segments[i];
    if (tMs >= s.end_ms - endSlackMs) {
      const next = segments[i + 1];
      if (next) return next.start_ms / 1000;
      return null;
    }
  }
  return null;
}

/** Snap to first segment if playhead is in dead time. */
export function snapToPointPlayback(segments, timeSec) {
  if (!segments.length) return num(timeSec, 0);
  const idx = findPointSegmentIndex(segments, timeSec);
  if (idx >= 0) return num(timeSec, 0);
  const tMs = Math.round((Number(timeSec) || 0) * 1000);
  for (const s of segments) {
    if (tMs < s.start_ms) return s.start_ms / 1000;
  }
  return segments[0].start_ms / 1000;
}

export { pointSegmentsFromPoseResult, resolvePointSegmentsFromPoseResult };
